import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Eye, Users, MousePointer, TrendingUp, Smartphone, Monitor, Loader2, Link2, ArrowRight } from 'lucide-react';

const API = `${window.location.origin}/api`;

const SOURCE_LABELS = { direct: 'Direto', whatsapp: 'WhatsApp', instagram: 'Instagram', facebook: 'Facebook', google: 'Google', other: 'Outro' };
const SOURCE_COLORS = { direct: '#6366F1', whatsapp: '#25D366', instagram: '#E1306C', facebook: '#1877F2', google: '#EA4335', other: '#94A3B8' };

export default function LinksAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/reports/links-analytics`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-400">Sem dados de analytics</div>;

  const s = data.summary;
  const maxViews = Math.max(...(data.daily_views || []).map(d => d.views), 1);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Acessos', value: s.total_views, icon: Eye, color: 'text-blue-600', bg: 'bg-blue-100' },
          { label: 'Visitantes Únicos', value: s.unique_visitors, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-100' },
          { label: 'Conversões', value: s.total_conversions, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100' },
          { label: 'Cliques (Linktree)', value: s.total_clicks, icon: MousePointer, color: 'text-purple-600', bg: 'bg-purple-100' },
          { label: 'Taxa Conversão', value: `${s.avg_conversion_rate}%`, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-100' },
        ].map((c, i) => (
          <Card key={i} className="bg-white border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-[10px] sm:text-xs text-slate-500 mb-1">{c.label}</p>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`w-4 h-4 ${c.color}`} /></div>
                <p className={`text-xl font-bold ${c.color}`}>{c.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily Views Chart */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center"><Eye className="w-5 h-5 text-blue-600" /></div>
            <div>
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Acessos (30 dias)</h2>
              <p className="text-xs text-slate-500">Visualizações diárias de todas as páginas</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-end gap-[2px]">
            {(data.daily_views || []).map((d, i) => {
              const h = maxViews > 0 ? Math.max((d.views / maxViews) * 100, 2) : 2;
              return (
                <TooltipProvider key={i} delayDuration={0}><Tooltip><TooltipTrigger asChild>
                  <div className="flex-1 rounded-t cursor-default transition-colors hover:opacity-80" style={{ height: `${h}%`, backgroundColor: d.views > 0 ? '#6366F1' : '#E2E8F0' }} />
                </TooltipTrigger><TooltipContent><p className="text-xs">{d.date}: {d.views} acessos</p></TooltipContent></Tooltip></TooltipProvider>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[9px] text-slate-400">{data.daily_views?.[0]?.date}</span>
            <span className="text-[9px] text-slate-400">{data.daily_views?.[data.daily_views.length - 1]?.date}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Devices */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <h3 className="text-sm font-semibold text-slate-900">Dispositivos</h3>
          </CardHeader>
          <CardContent>
            {Object.entries(data.devices || {}).map(([device, count]) => {
              const total = Object.values(data.devices).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={device} className="flex items-center gap-3 mb-3 last:mb-0">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    {device === 'mobile' ? <Smartphone className="w-4 h-4 text-slate-600" /> : <Monitor className="w-4 h-4 text-slate-600" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700 capitalize">{device === 'mobile' ? 'Mobile' : 'Desktop'}</span>
                      <span className="text-slate-500">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 rounded-full"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} /></div>
                  </div>
                </div>
              );
            })}
            {Object.keys(data.devices || {}).length === 0 && <p className="text-xs text-slate-400 text-center py-4">Sem dados</p>}
          </CardContent>
        </Card>

        {/* Sources */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <h3 className="text-sm font-semibold text-slate-900">Fontes de Acesso</h3>
          </CardHeader>
          <CardContent>
            {Object.entries(data.sources || {}).sort(([,a],[,b]) => b - a).map(([source, count]) => {
              const total = Object.values(data.sources).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={source} className="flex items-center gap-3 mb-3 last:mb-0">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[source] || '#94A3B8' }} />
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700">{SOURCE_LABELS[source] || source}</span>
                      <span className="text-slate-500">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-200 rounded-full"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: SOURCE_COLORS[source] || '#94A3B8' }} /></div>
                  </div>
                </div>
              );
            })}
            {Object.keys(data.sources || {}).length === 0 && <p className="text-xs text-slate-400 text-center py-4">Sem dados</p>}
          </CardContent>
        </Card>
      </div>

      {/* Per-link stats */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-100 flex items-center justify-center"><Link2 className="w-5 h-5 text-cyan-600" /></div>
            <div>
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Desempenho por Link</h2>
              <p className="text-xs text-slate-500">Acessos, conversões e taxa por página</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Página</th>
                  <th className="text-center py-2 text-xs font-semibold text-slate-500 uppercase">Cliques</th>
                  <th className="text-center py-2 text-xs font-semibold text-slate-500 uppercase">Únicos</th>
                  <th className="text-center py-2 text-xs font-semibold text-slate-500 uppercase">Conversões</th>
                  <th className="text-center py-2 text-xs font-semibold text-slate-500 uppercase">Taxa</th>
                  <th className="text-center py-2 text-xs font-semibold text-slate-500 uppercase">7 dias</th>
                </tr>
              </thead>
              <tbody>
                {data.links.map(link => {
                  const totalClicks = (link.views || 0) + (link.clicks || 0);
                  const isExternal = !link.system_type && !link.slug;
                  return (
                  <tr key={link.link_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{link.titulo || link.slug}</span>
                        {link.system_type && <Badge className="bg-slate-100 text-slate-500 text-[8px]">{link.system_type}</Badge>}
                        {isExternal && (
                          <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                            <Badge className="bg-amber-50 text-amber-500 text-[8px] cursor-default">externo</Badge>
                          </TooltipTrigger><TooltipContent><p className="text-xs">Link externo — apenas cliques no Linktree são contabilizados.</p></TooltipContent></Tooltip></TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className="text-center py-2.5 font-semibold text-slate-700">{totalClicks}</td>
                    <td className="text-center py-2.5 text-slate-500">{link.unique_views}</td>
                    <td className="text-center py-2.5">
                      <Badge className={`${link.conversions > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'} text-xs`}>{link.conversions}</Badge>
                    </td>
                    <td className="text-center py-2.5">
                      <span className={`text-xs font-semibold ${link.conversion_rate >= 10 ? 'text-green-600' : link.conversion_rate > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{link.conversion_rate}%</span>
                    </td>
                    <td className="text-center py-2.5 text-xs text-slate-500">{link.views_7d}</td>
                  </tr>
                  );
                })}
                {data.links.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">Sem dados de analytics</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
